<template>
  <div class="vm-template-vue-results" @click="handlePaginationClick">
    <div v-if="isLoading" class="vm-template-vue-results-overlay">
      <div class="vm-template-vue-results-loader">
        <span class="vm-template-vue-results-spinner" aria-hidden="true"></span>
        <span>Loading template VM list...</span>
      </div>
    </div>

    <AppPagination
      :pagination="data.pagination"
      aria-label="Template list pagination"
      key-prefix="template-page"
      extra-class="text-center"
    />

    <table class="table table-striped table-bordered table-condensed">
      <thead>
        <tr>
          <th><div class="text-center">OS</div></th>
          <th><div class="text-center">Name</div></th>
          <th class="hidden-tablet"><div class="text-center">CPU</div></th>
          <th class="hidden-tablet"><div class="text-center">Memory</div></th>
          <th class="hidden-note"><div class="text-center">Boot</div></th>
          <th class="hidden-tablet"><div class="text-center">Disk</div></th>
          <th class="hidden-phone"><div class="text-center">Network</div></th>
          <th><div class="text-center">Created by</div></th>
          <th class="vm-template-actions-col"><div class="text-center">Create</div></th>
          <th class="vm-template-actions-col"><div class="text-center">Actions</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in data.rows || []" :key="`template-${row.id}`" :id="`vm-row-${row.id}`">
          <td><div class="text-center"><AppOsIcon :symbol="row.os_icon_symbol" /></div></td>
          <td>{{ row.name }}</td>
          <td class="hidden-tablet"><div class="text-center">{{ row.vcpu }}</div></td>
          <td class="hidden-tablet"><div class="text-center">{{ row.memory }}</div></td>
          <td class="hidden-note"><div class="text-center">{{ row.boot }}</div></td>
          <td class="hidden-tablet">
            <div class="text-center">{{ row.disk }}</div>
            <a
              v-if="row.storage_migration?.active"
              :href="row.storage_migration.href"
              class="vm-template-storage-migration"
              :title="row.storage_migration.title"
            >
              <span class="vm-template-storage-migration__spinner" aria-hidden="true"></span>
              <span>{{ row.storage_migration.label }}</span>
              <span class="vm-template-storage-migration__percent">{{ row.storage_migration.percent }}%</span>
            </a>
          </td>
          <td class="hidden-phone"><div class="text-center">{{ row.network }}</div></td>
          <td><div class="text-center">{{ row.owner }}</div></td>
          <td class="vm-template-actions-col">
            <div class="text-center vm-template-actions">
              <div class="app-action-buttons vm-template-create-split">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary app-action-btn app-action-btn--primary"
                  title="Create VM"
                  :disabled="!row.actions?.clone_url || row.storage_migration?.active"
                  @click="handleClone(row)"
                >
                  <AppIcon name="play" />
                </button>
                <button
                  v-if="row.actions?.configure_clone_url"
                  type="button"
                  class="btn btn-sm btn-outline-primary app-action-btn vm-template-create-split__toggle"
                  :title="row.storage_migration?.active ? 'Storage migration is in progress' : 'Configure cloud-init'"
                  aria-label="Configure cloud-init"
                  :aria-expanded="openCreateMenuId === row.id ? 'true' : 'false'"
                  :disabled="row.storage_migration?.active"
                  @click.stop="toggleCreateMenu(row.id, $event)"
                >
                  <AppIcon name="chevron-down" />
                </button>
                <Teleport to="body">
                  <div
                    v-if="row.actions?.configure_clone_url && openCreateMenuId === row.id"
                    class="vm-template-actions__menu"
                    :style="createMenuStyle"
                    @click.stop
                  >
                    <a :href="row.actions?.configure_clone_url" class="vm-template-actions__item">
                      Configure cloud-init
                    </a>
                  </div>
                </Teleport>
              </div>
            </div>
          </td>
          <td class="vm-template-actions-col">
            <div v-if="row.actions?.can_manage" class="text-center vm-template-actions__menu-wrap">
              <button
                type="button"
                class="vm-template-actions__gear"
                :aria-expanded="openMenuId === row.id ? 'true' : 'false'"
                @click.stop="toggleMenu(row.id, $event)"
              >
                <AppIcon name="settings" />
                <AppIcon name="chevron-down" class="vm-template-actions__caret" />
              </button>

              <div
                v-if="openMenuId === row.id"
                class="vm-template-actions__menu"
                :style="menuStyle"
                @click.stop
              >
                <a :href="row.actions.edit_url" class="vm-template-actions__item">
                  <AppIcon name="edit" />
                  <span>Edit</span>
                </a>

                <a :href="row.actions.storage_migration_url" class="vm-template-actions__item">
                  <AppIcon name="disk" />
                  <span>{{ row.actions.storage_migration_label }}</span>
                </a>

                <button
                  type="button"
                  class="vm-template-actions__item vm-template-actions__item--danger"
                  @click.stop="handleDelete(row)"
                >
                  <AppIcon name="delete" />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import AppIcon from './AppIcon.vue'
import AppOsIcon from './AppOsIcon.vue'
import AppPagination from './AppPagination.vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ rows: [], pagination: null })
  },
  initialLoading: {
    type: Boolean,
    default: false
  },
  endpoint: {
    type: String,
    default: ''
  }
})

const data = ref(props.initialData || { rows: [], pagination: null })
const isLoading = ref(!!props.initialLoading)
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
const openMenuId = ref(null)
const openCreateMenuId = ref(null)
const createMenuStyle = ref({})
const menuStyle = ref({})
let storageMigrationTimer = null
let storageMigrationPollInFlight = false

function sccBeginClonePolling(statusUrl) {
  window.sccStatusShell?.sccBeginClonePolling?.(statusUrl)
}

function sccShowCloneOverlay(vmName) {
  window.sccStatusShell?.sccShowCloneOverlay?.(vmName)
}

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}

function sccStartStatusPollingFromResponse(data, options = {}) {
  return window.sccStatusShell?.sccStartStatusPollingFromResponse?.(data, options)
}

function markCloneProgressFailed(message) {
  window.sccStatusShell?.markCloneProgressFailed?.(message)
}

function handleUpdate(event) {
  data.value = event.detail || { rows: [], pagination: null }
  closeMenu()
}

function handleLoading(event) {
  const payload = event.detail || {}
  if (payload.eventName !== 'scc:vm-template-results:update') {
    return
  }

  isLoading.value = !!payload.loading
}

async function pollStorageMigrations() {
  if (storageMigrationPollInFlight) return
  const activeRows = (data.value.rows || []).filter((row) => row.storage_migration?.active && row.storage_migration?.status_url)
  if (!activeRows.length) return

  storageMigrationPollInFlight = true
  try {
    const results = await Promise.allSettled(activeRows.map(async (row) => ({
      vmId: row.id,
      progress: await requestJson(row.storage_migration.status_url, { method: 'GET' })
    })))
    const updates = new Map(results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => [String(result.value.vmId), result.value.progress]))
    if (!updates.size) return

    const rows = (data.value.rows || []).map((existingRow) => {
      const progress = updates.get(String(existingRow.id))
      if (!progress || !existingRow.storage_migration?.active) return existingRow
      const row = { ...existingRow }
      if (progress.status === 'completed' || progress.status === 'failed') {
        row.storage_migration = null
        row.actions = {
          ...row.actions,
          storage_migration_url: existingRow.storage_migration.migrate_url,
          storage_migration_label: 'Migrate Storage'
        }
      } else {
        row.storage_migration = {
          ...row.storage_migration,
          percent: Math.max(0, Math.min(100, Number(progress.percent) || 0))
        }
      }
      return row
    })
    data.value = { ...data.value, rows }
  } finally {
    storageMigrationPollInFlight = false
  }
}

async function handlePaginationClick(event) {
  const link = event.target.closest('.pagination a[data-page]')
  if (!link) {
    return
  }

  event.preventDefault()
  const page = link.dataset.page
  if (!page) {
    return
  }

  const url = new URL(props.endpoint || window.location.href, window.location.origin)
  url.searchParams.set('page', page)

  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const payload = await response.json()
    data.value = payload?.results_data || { rows: [], pagination: null }
    closeMenu()

    if ((!props.endpoint || props.endpoint === '') && window.history && window.history.replaceState) {
      window.history.replaceState({}, '', url.toString())
    }
  } catch (error) {
    console.error('Failed to update template pagination', error)
    window.alert('Failed to update the template list.')
  }
}

function toggleMenu(rowId, event) {
  if (openMenuId.value === rowId) {
    closeMenu()
    return
  }

  const trigger = event?.currentTarget
  const rect = trigger?.getBoundingClientRect()
  if (!rect) {
    openMenuId.value = rowId
    menuStyle.value = {}
    return
  }

  const menuWidth = 170
  const menuHeight = 154
  const gap = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = rect.right - menuWidth
  let top = rect.bottom + gap

  if (left + menuWidth > viewportWidth - 12) left = viewportWidth - menuWidth - 12
  if (left < 12) left = 12
  if (top + menuHeight > viewportHeight - 12) top = rect.top - menuHeight - gap
  if (top < 12) top = 12

  openMenuId.value = rowId
  menuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`
  }
}

function closeMenu() {
  openMenuId.value = null
  openCreateMenuId.value = null
  createMenuStyle.value = {}
  menuStyle.value = {}
}

function handleDocumentClick(event) {
  if (!event.target.closest('.vm-template-actions__menu') && !event.target.closest('.vm-template-actions__gear') && !event.target.closest('.vm-template-create-split')) {
    closeMenu()
  }
}

function toggleCreateMenu(rowId, event) {
  const row = (data.value.rows || []).find((entry) => String(entry.id) === String(rowId))
  if (row?.storage_migration?.active) return
  if (openCreateMenuId.value === rowId) {
    closeMenu()
    return
  }

  const rect = event?.currentTarget?.getBoundingClientRect()
  openMenuId.value = null
  openCreateMenuId.value = rowId
  if (!rect) {
    createMenuStyle.value = {}
    return
  }

  const menuWidth = 180
  const menuHeight = 40
  const gap = 4
  let left = rect.right - menuWidth
  let top = rect.bottom + gap
  if (left < 12) left = 12
  if (left + menuWidth > window.innerWidth - 12) left = window.innerWidth - menuWidth - 12
  if (top + menuHeight > window.innerHeight - 12) top = rect.top - menuHeight - gap
  createMenuStyle.value = {
    position: 'fixed',
    zIndex: 5000,
    top: `${top}px`,
    left: `${left}px`,
    right: 'auto'
  }
}

async function handleClone(row) {
  const cloneUrl = row.actions?.clone_url
  const cloneName = row.actions?.clone_name || row.name || 'VM'
  if (!cloneUrl || row.storage_migration?.active) {
    return
  }

  sccShowCloneOverlay(cloneName)

  try {
    const payload = await requestJson(cloneUrl, { method: 'GET' })
    sccStartStatusPollingFromResponse(payload, {
      beginPolling: sccBeginClonePolling,
      missingStatusUrlMessage: 'Clone progress URL was not returned.'
    })
  } catch (error) {
    markCloneProgressFailed(error.message || 'Failed to start clone.')
  }
}

async function handleDelete(row) {
  const actions = row.actions || {}
  if (!actions.destroy_url) {
    return
  }

  if (actions.destroy_confirm_message && !window.confirm(actions.destroy_confirm_message)) {
    return
  }

  try {
    const payload = await requestJson(actions.destroy_url, { method: 'DELETE' })
    if (payload && payload.level && payload.message) {
      sccShowToast(payload.level, payload.message)
    }

    data.value = {
      ...data.value,
      rows: (data.value.rows || []).filter((entry) => String(entry.id) !== String(row.id))
    }
    closeMenu()
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to delete VM.')
  }
}

async function requestJson(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(options.headers || {})
  }
  const fetchOptions = {
    method,
    headers,
    credentials: 'same-origin'
  }

  if (csrfToken && method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRF-Token'] = csrfToken
  }

  if (options.body) {
    fetchOptions.body = options.body
  }

  const response = await fetch(url, fetchOptions)
  const text = await response.text()
  let payload = null

  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    throw new Error(readResponseMessage(payload, text, options.fallbackError))
  }

  return payload
}

function readResponseMessage(payload, text, fallbackMessage) {
  if (payload && payload.message) {
    return payload.message
  }

  if (text) {
    return text
  }

  return fallbackMessage || 'Request failed.'
}

onMounted(() => {
  document.addEventListener('scc:vm-template-results:update', handleUpdate)
  document.addEventListener('scc:lazy-results:loading', handleLoading)
  document.addEventListener('click', handleDocumentClick)
  window.addEventListener('resize', closeMenu)
  window.addEventListener('scroll', closeMenu, true)
  pollStorageMigrations()
  storageMigrationTimer = window.setInterval(pollStorageMigrations, 1000)
})

onBeforeUnmount(() => {
  document.removeEventListener('scc:vm-template-results:update', handleUpdate)
  document.removeEventListener('scc:lazy-results:loading', handleLoading)
  document.removeEventListener('click', handleDocumentClick)
  window.removeEventListener('resize', closeMenu)
  window.removeEventListener('scroll', closeMenu, true)
  if (storageMigrationTimer) {
    window.clearInterval(storageMigrationTimer)
    storageMigrationTimer = null
  }
})
</script>

<style scoped>
.vm-template-vue-results {
  position: relative;
  min-height: 120px;
}

.vm-template-vue-results-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 253, 0.82);
  backdrop-filter: blur(1px);
}

.vm-template-vue-results-loader {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid rgba(130, 150, 175, 0.24);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #36506a;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 16px 34px rgba(122, 141, 164, 0.16);
}

.vm-template-vue-results-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(116, 143, 171, 0.22);
  border-top-color: #4d6d8f;
  border-radius: 999px;
  animation: vm-template-spin 0.9s linear infinite;
}

.vm-template-storage-migration {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 6px;
  color: #2563a6;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
  white-space: nowrap;
}

.vm-template-storage-migration:hover,
.vm-template-storage-migration:focus {
  color: #174f87;
  text-decoration: underline;
}

.vm-template-storage-migration__spinner {
  width: 10px;
  height: 10px;
  border: 2px solid #bfd5ea;
  border-top-color: #2563a6;
  border-radius: 50%;
  animation: vm-template-spin 0.8s linear infinite;
}

.vm-template-storage-migration__percent {
  color: #5d7287;
  font-variant-numeric: tabular-nums;
}

@keyframes vm-template-spin {
  to {
    transform: rotate(360deg);
  }
}

.vm-template-actions__menu-wrap {
  position: relative;
}

.vm-template-actions__gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 44px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d4dde8;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #eef3f8 100%);
  color: #4a5f75;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.vm-template-actions__gear:hover,
.vm-template-actions__gear:focus {
  transform: translateY(-1px);
  border-color: #b8c9da;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
}

.vm-template-actions__caret {
  font-size: 11px;
  opacity: 0.75;
}

.vm-template-actions__menu {
  z-index: 60;
  min-width: 170px;
  padding: 8px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 38px rgba(15, 23, 42, 0.16);
}

.vm-template-actions__form {
  display: block;
}

.vm-template-actions__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #425466;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
}

.vm-template-actions__item:hover {
  background: #eff4f9;
  color: #27445f;
  text-decoration: none;
}

.vm-template-actions__item--danger,
button.vm-template-actions__item--danger {
  color: #9f1d2d;
}

.vm-template-actions__item--danger:hover,
button.vm-template-actions__item--danger:hover {
  background: #fff1f2;
  color: #7f1722;
}
</style>
